import { useQuery } from "@tanstack/react-query";
import { createClient } from "@supabase/supabase-js";

// Hub (Life Science DB) read-only client
const HUB_URL = "https://mjsgtszehjltxmbxtctz.supabase.co";
const HUB_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1qc2d0c3plaGpsdHhtYnh0Y3R6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzY5NjcxMjAsImV4cCI6MjA1MjU0MzEyMH0.fl_dymBOFsaN-MHhMp6GJJjl0JGhJFAXvjqPUNFMn7Q";

const hubClient = createClient(HUB_URL, HUB_ANON_KEY);

// Corporate suffixes to strip for fuzzy matching
const CORP_SUFFIXES = /\s+(Inc\.?|LLC|Ltd\.?|PLC|AG|AB|SE|SA|GmbH|Co\.?|Corp\.?|Corporation|Incorporated|Limited|Therapeutics|Pharmaceuticals|Biotech|Biosciences|Sciences|Pharma|Medical|Healthcare)$/gi;

/**
 * Strip corporate suffixes to get a core company name for matching.
 * e.g. "Novartis Pharmaceuticals AG" → "Novartis"
 * e.g. "Bristol-Myers Squibb" → "Bristol-Myers Squibb" (no suffix)
 * e.g. "Gilead Sciences/Kite" → "Gilead" (splits on /)
 */
function getSearchNames(companyName: string): string[] {
  const names = new Set<string>();

  // Original name
  names.add(companyName.trim());

  // Handle slash-separated names (e.g. "Gilead Sciences/Kite")
  if (companyName.includes("/")) {
    companyName.split("/").forEach(part => names.add(part.trim()));
  }

  // Strip suffixes iteratively (some names have multiple: "Novartis Pharmaceuticals AG")
  const stripped = companyName.replace(/\s*\/.*$/, ""); // remove /Kite part first
  let core = stripped;
  for (let i = 0; i < 3; i++) {
    const prev = core;
    core = core.replace(CORP_SUFFIXES, "").trim();
    if (core === prev) break;
  }
  if (core.length >= 3 && core !== companyName.trim()) {
    names.add(core);
  }

  return Array.from(names).filter(n => n.length >= 3);
}

export interface HubClinicalTrial {
  id: string;
  nct_id: string;
  title: string;
  official_title: string | null;
  brief_summary: string | null;
  overall_status: string | null;
  phase: string | null;
  study_type: string | null;
  enrollment: number | null;
  start_date: string | null;
  completion_date: string | null;
  lead_sponsor_name: string | null;
  conditions: string[] | null;
  interventions: any | null;
  therapeutic_area: string | null;
  modality: string | null;
  location_countries: string[] | null;
}

export interface HubNIHGrant {
  id: string;
  project_number: string | null;
  title: string | null;
  abstract: string | null;
  pi_names: string[] | null;
  organization_name: string | null;
  award_amount: number | null;
  fiscal_year: number | null;
  project_start_date: string | null;
  project_end_date: string | null;
  funding_mechanism: string | null;
  is_active: boolean | null;
}

export interface HubPubMedArticle {
  id: string;
  pmid: string | null;
  title: string | null;
  abstract: string | null;
  authors: string[] | null;
  journal: string | null;
  pubdate: string | null;
  pub_year: number | null;
  therapeutic_area: string | null;
  modality: string | null;
}

export interface HubSECFiling {
  id: string;
  accession_number: string | null;
  company_name: string | null;
  ticker: string | null;
  filing_type: string | null;
  filing_date: string | null;
  document_url: string | null;
  description: string | null;
  ai_summary: string | null;
}

export interface HubPatent {
  id: string;
  patent_number: string | null;
  patent_title: string | null;
  patent_abstract: string | null;
  patent_date: string | null;
  application_date: string | null;
  assignee_organization: string | null;
  therapeutic_area: string | null;
  modality: string | null;
  citation_count: number | null;
  claims_count: number | null;
}

/**
 * Look up the Hub account name using life_science_id.
 * Returns the Hub name so we can use it as an alternate search term.
 */
export function useHubAccountName(lifeScienceId: string | null) {
  return useQuery({
    queryKey: ["hub-account-name", lifeScienceId],
    queryFn: async () => {
      if (!lifeScienceId) return null;
      const { data, error } = await hubClient
        .from("new_accounts")
        .select("name, domain")
        .eq("id", lifeScienceId)
        .single();
      if (error) return null;
      return data as { name: string; domain: string | null } | null;
    },
    enabled: !!lifeScienceId,
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * Fetch clinical trials from Hub DB matching a company name (lead_sponsor_name).
 * Uses multiple search variants (original name, Hub name, stripped core name)
 * to maximize matches.
 */
export function useHubClinicalTrials(companyName: string | null, hubAccountName: string | null) {
  return useQuery({
    queryKey: ["hub-clinical-trials", companyName, hubAccountName],
    queryFn: async () => {
      if (!companyName) return [];

      // Build search name variants from both CARR name and Hub name
      const allNames = getSearchNames(companyName);
      if (hubAccountName) {
        getSearchNames(hubAccountName).forEach(n => {
          if (!allNames.includes(n)) allNames.push(n);
        });
      }

      // Build OR filter: lead_sponsor_name.ilike.%name1%,lead_sponsor_name.ilike.%name2%,...
      const orFilter = allNames.map(n => `lead_sponsor_name.ilike.%${n}%`).join(",");

      const { data, error } = await hubClient
        .from("clinical_trials")
        .select("id, nct_id, title, official_title, brief_summary, overall_status, phase, study_type, enrollment, start_date, completion_date, lead_sponsor_name, conditions, interventions, therapeutic_area, modality, location_countries")
        .or(orFilter)
        .order("start_date", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data || []) as HubClinicalTrial[];
    },
    enabled: !!companyName,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch NIH grants from Hub DB matching a company/organization name.
 * Uses multiple search variants for broader matching.
 */
export function useHubNIHGrants(companyName: string | null, hubAccountName: string | null) {
  return useQuery({
    queryKey: ["hub-nih-grants", companyName, hubAccountName],
    queryFn: async () => {
      if (!companyName) return [];

      const allNames = getSearchNames(companyName);
      if (hubAccountName) {
        getSearchNames(hubAccountName).forEach(n => {
          if (!allNames.includes(n)) allNames.push(n);
        });
      }

      const orFilter = allNames.map(n => `organization_name.ilike.%${n}%`).join(",");

      const { data, error } = await hubClient
        .from("nih_grants")
        .select("id, project_number, title, abstract, pi_names, organization_name, award_amount, fiscal_year, project_start_date, project_end_date, funding_mechanism, is_active")
        .or(orFilter)
        .order("fiscal_year", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data || []) as HubNIHGrant[];
    },
    enabled: !!companyName,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch PubMed articles from Hub DB matching a company name
 */
export function useHubPubMedArticles(companyName: string | null, hubAccountName: string | null = null) {
  return useQuery({
    queryKey: ["hub-pubmed", companyName, hubAccountName],
    queryFn: async () => {
      if (!companyName) return [];

      const allNames = getSearchNames(companyName);
      if (hubAccountName) {
        getSearchNames(hubAccountName).forEach(n => {
          if (!allNames.includes(n)) allNames.push(n);
        });
      }

      const orFilter = allNames.flatMap(n => [
        `affiliations.ilike.%${n}%`,
        `title.ilike.%${n}%`,
      ]).join(",");

      const { data, error } = await hubClient
        .from("pubmed_articles")
        .select("id, pmid, title, abstract, authors, journal, pubdate, pub_year, therapeutic_area, modality")
        .or(orFilter)
        .order("pub_year", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data || []) as HubPubMedArticle[];
    },
    enabled: !!companyName,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch SEC filings from Hub DB matching a company name.
 * SEC filings cover 21 public CGT companies.
 */
export function useHubSECFilings(companyName: string | null, hubAccountName: string | null) {
  return useQuery({
    queryKey: ["hub-sec-filings", companyName, hubAccountName],
    queryFn: async () => {
      if (!companyName) return [];

      const allNames = getSearchNames(companyName);
      if (hubAccountName) {
        getSearchNames(hubAccountName).forEach(n => {
          if (!allNames.includes(n)) allNames.push(n);
        });
      }

      const orFilter = allNames.map(n => `company_name.ilike.%${n}%`).join(",");

      const { data, error } = await hubClient
        .from("sec_filings")
        .select("id, accession_number, company_name, ticker, filing_type, filing_date, document_url, description, ai_summary")
        .or(orFilter)
        .order("filing_date", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data || []) as HubSECFiling[];
    },
    enabled: !!companyName,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch patents from Hub DB matching a company/organization name.
 * Patents cover 159 assignee organizations.
 */
export function useHubPatents(companyName: string | null, hubAccountName: string | null) {
  return useQuery({
    queryKey: ["hub-patents", companyName, hubAccountName],
    queryFn: async () => {
      if (!companyName) return [];

      const allNames = getSearchNames(companyName);
      if (hubAccountName) {
        getSearchNames(hubAccountName).forEach(n => {
          if (!allNames.includes(n)) allNames.push(n);
        });
      }

      const orFilter = allNames.map(n => `assignee_organization.ilike.%${n}%`).join(",");

      const { data, error } = await hubClient
        .from("patents")
        .select("id, patent_number, patent_title, patent_abstract, patent_date, application_date, assignee_organization, therapeutic_area, modality, citation_count, claims_count")
        .or(orFilter)
        .order("patent_date", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data || []) as HubPatent[];
    },
    enabled: !!companyName,
    staleTime: 10 * 60 * 1000,
  });
}
